import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { Clip } from '@/types/clip.types';
import {
  RefreshCw,
  AlertTriangle,
  Pencil,
  Image as ImageIcon,
  MoreVertical,
  Archive,
  Trash2,
  Lock,
} from 'lucide-react';
import { StatusBadge } from '@/components/StatusBadge';
import { InlineEpisodeEditor } from '@/components/InlineEpisodeEditor';
import { motion } from 'framer-motion';
import type { UserPresence } from '@/types/presence.types';

interface EpisodeHeaderCardProps {
  episode: PodcastEpisode;
  clips: Clip[];
  artworkUrl: string | null;
  isPublished: boolean;
  isLockedByOther: boolean;
  lockHolder: UserPresence | null;
  onEditDetails: () => void;
  onSaveDetails: (title: string, description: string) => Promise<void>;
  onArchive: () => void;
  onDelete: () => void;
  onRefresh: () => void;
  getCompanyName: (companyId: string) => string | undefined;
  isEditorExpanded: boolean;
  onEditorExpandedChange: (expanded: boolean) => void;
}

export const EpisodeHeaderCard: React.FC<EpisodeHeaderCardProps> = ({
  episode,
  clips,
  artworkUrl,
  isPublished,
  isLockedByOther,
  lockHolder,
  onEditDetails,
  onSaveDetails,
  onArchive,
  onDelete,
  onRefresh,
  getCompanyName,
  isEditorExpanded,
  onEditorExpandedChange,
}) => {
  const isEditable = !isPublished && !isLockedByOther;

  return (
    <div className="max-w-content" style={{ paddingLeft: '32px', paddingRight: '32px', paddingTop: '24px', paddingBottom: '24px' }}>
      <div className="bg-podcast rounded-lg border border-border border-l-[3px] border-l-primary shadow-sm">
        {/* Header content area - has hover effect */}
        <div
          className={`transition-colors rounded-lg ${isEditable ? 'group' : ''}`}
          style={{ padding: '20px 24px 24px 24px', backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            if (isEditable) e.currentTarget.style.backgroundColor = 'var(--pm-green-bg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div>
            <div className="flex items-start justify-between">
              {/* Clickable header area */}
              <div
                className="flex-1 min-w-0"
                style={{ cursor: isEditable ? 'pointer' : 'default' }}
                onClick={isEditable ? onEditDetails : undefined}
              >
                <div className="flex items-center gap-3">
                  {/* Artwork */}
                  <div className="w-10 h-10 shrink-0">
                    {artworkUrl ? (
                      <motion.img
                        key={artworkUrl}
                        src={artworkUrl}
                        alt="Episode artwork"
                        className="w-10 h-10 rounded-lg object-cover"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 20, duration: 0.3 }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div className="flex items-center gap-2 min-w-0">
                    <h1 className="text-h1 truncate" style={{ color: 'var(--pm-text-primary)' }}>{episode.title}</h1>
                    {isEditable && (
                      <Pencil className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" style={{ color: 'var(--pm-green)' }} />
                    )}
                  </div>
                </div>

                {/* Company Name Badge */}
                {getCompanyName(episode.companyId) && (
                  <div className="mt-2 ml-[52px]">
                    <Badge variant="secondary" className="text-xs">
                      {getCompanyName(episode.companyId)}
                    </Badge>
                  </div>
                )}

                {/* Description Row */}
                <div className="mt-2 ml-[52px]">
                  {episode.description ? (
                    <p
                      className="text-sm line-clamp-2 transition-colors group-hover:text-[var(--pm-text-muted)]"
                      style={{ color: 'var(--pm-text-muted)' }}
                    >
                      {episode.description}
                    </p>
                  ) : isEditable ? (
                    <span
                      className="text-sm flex items-center gap-1.5 group-hover:text-amber-300 transition-colors"
                      style={{ color: 'var(--pm-text-muted)' }}
                    >
                      <AlertTriangle className="h-3 w-3" />
                      No description - click to add
                    </span>
                  ) : (
                    <p className="text-sm italic" style={{ color: 'var(--pm-text-faint)' }}>No description</p>
                  )}
                </div>
              </div>

              {/* Right side: Status badge, lock indicator, and actions dropdown */}
              <div className="shrink-0 ml-4 flex items-center gap-6">
                <StatusBadge status={episode.status} scheduledPublishDate={episode.scheduledPublishDate} data-testid="episode-status-badge" />

                {isLockedByOther && lockHolder && (
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/20 text-amber-400 text-xs font-medium">
                    <Lock className="h-3 w-3" />
                    <span>Locked by {lockHolder.displayName}</span>
                  </div>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 rounded-full ring-1 ring-offset-1 ring-offset-background ${episode.status === EpisodeStatus.ARCHIVED ? 'animate-pulse ring-2 ring-primary ring-offset-2' : 'ring-zinc-600'}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onRefresh}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => !isLockedByOther && onArchive()} disabled={isLockedByOther}>
                      <Archive className="h-4 w-4 mr-2" />
                      {episode.status === EpisodeStatus.ARCHIVED ? 'Unarchive' : 'Archive'}
                    </DropdownMenuItem>
                    {episode.status === EpisodeStatus.ARCHIVED && (
                      <DropdownMenuItem
                        onClick={() => !isLockedByOther && onDelete()}
                        disabled={isLockedByOther}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>

        {/* Inline Episode Editor */}
        <InlineEpisodeEditor
          isExpanded={isEditorExpanded}
          onExpandedChange={onEditorExpandedChange}
          episode={episode}
          clips={clips}
          onSave={onSaveDetails}
          isLocked={isLockedByOther}
        />
      </div>
    </div>
  );
};
